// src/app/core/auth.service.ts
import { Injectable } from '@angular/core';
import { Auth, onAuthStateChanged, User as FirebaseUser, signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut, sendPasswordResetEmail, authState } from '@angular/fire/auth';
import { setPersistence, browserLocalPersistence, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { Firestore, collection, query, where, getDocs, doc, setDoc, serverTimestamp, getDoc, addDoc } from '@angular/fire/firestore';
import { BehaviorSubject, Observable, from, of } from 'rxjs';
import { tap, catchError, filter, take, map, first, switchMap, shareReplay } from 'rxjs/operators';
import { Router } from '@angular/router';
import { User } from './models';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private userSubject = new BehaviorSubject<User | null>(null);
  user$ = this.userSubject.asObservable();
  isManager$: Observable<boolean>;
  isLoggedIn$: Observable<boolean>;

  private _authStateReady = new BehaviorSubject<boolean>(false);
  public authStateReady$: Observable<boolean> = this._authStateReady.asObservable().pipe(
    filter(isReady => isReady === true),
    take(1)
  );

   constructor(
    private afAuth: Auth,
    private firestore: Firestore,
    private router: Router
  ) {
    // Sincronización reactiva del usuario de Auth con el perfil de Firestore
    this.user$ = authState(this.afAuth).pipe(
      switchMap((fbUser: FirebaseUser | null) => {
        if (fbUser) {
          return this.getUserProfile(fbUser);
        } else {
          return of(null);
        }
      }),
      tap(() => this._authStateReady.next(true)),
      shareReplay(1)
    );

    this.isManager$ = this.user$.pipe(
      map(u => u?.role === 'manager')
    );
    this.isLoggedIn$ = this.user$.pipe(map(user => !!user));
  }

  private getUserProfile(fbUser: FirebaseUser): Observable<User> {
    const ref = doc(this.firestore, `bap_users/${fbUser.uid}`);
    return from(getDoc(ref)).pipe(
      map(snap => {
        if (snap.exists()) {
          return snap.data() as User;
        } else {
          // Fallback defensivo si el registro aún no se completa
          return { 
            uid: fbUser.uid, 
            email: fbUser.email, 
            name: fbUser.displayName || 'Usuario Bici a Punto', 
            role: 'client' as const 
          } as User;
        }
      })
    );
  }


  async login(email: string, password: string): Promise<void> {
    try {
      const usersCollectionRef = collection(this.firestore, 'bap_users');
      const q = query(usersCollectionRef, where('email', '==', email));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        const error: any = new Error('Usuario no registrado en el sistema.');
        error.code = 'auth/user-not-found-in-system';
        throw error;
      }
      
      const userCredential = await signInWithEmailAndPassword(this.afAuth, email, password);
      
      // Actualizar el userSubject manualmente aquí para evitar la condición de carrera con el AuthGuard
      const firebaseUser = userCredential.user;
      const appUser: User = {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        displayName: firebaseUser.displayName
      };
      this.userSubject.next(appUser);

      // Navegación explícita al home después de un login exitoso
      this.router.navigate(['/home']); 
    } catch (error) {
      console.error('Falló el ingreso:', error);
      throw error;
    }
  }

  async signup(email: string, password: string): Promise<void> {
    try {
      const userCredential = await createUserWithEmailAndPassword(this.afAuth, email, password);
      const firebaseUser = userCredential.user;
      
      // Usar el UID de Auth como ID del documento en bap_users
      const userDocRef = doc(this.firestore, `bap_users/${firebaseUser.uid}`);
      await setDoc(userDocRef, {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        createdAt: serverTimestamp()
      });

      // Cerrar la sesión para forzar al usuario a iniciar sesión por primera vez
      await signOut(this.afAuth);

    } catch (error) {
      console.error('Falló el registro:', error);
      throw error;
    }
  }

 private async redirectAfterLogin() {
    // Esperamos a que el observable de usuario emita el perfil de Firestore
    const user = await this.user$.pipe(
      filter(u => u !== null),
      first()
    ).toPromise();
    
    if (!user) return;

    // Si es cliente, va al portal. Si es staff, al home.
    if (user.role === 'client') {
      this.router.navigate(['/portal']);
    } else {
      this.router.navigate(['/home']);
    }
  }


  async loginWithGoogle(): Promise<void> {
    const provider = new GoogleAuthProvider();
    try {
      const credential = await signInWithPopup(this.afAuth, provider);
      
      // LA JUGADA CLAVE: Sincronizar con Firestore inmediatamente
      await this.updateUserData(credential.user);
      
      // Redirigir según el rol o dominio
      await this.redirectAfterLogin();
    } catch (error) {
      console.error("Error con Google Auth:", error);
      throw error;
    }
  }

    private async updateUserData(fbUser: FirebaseUser, manualRole?: 'client' | 'employee' | 'manager') {
    const userRef = doc(this.firestore, `bap_users/${fbUser.uid}`);
    const snap = await getDoc(userRef);
    const emailLower = fbUser.email?.toLowerCase() || '';
    
    let role: 'client' | 'employee' | 'manager' = 'client';
    const isPortal = window.location.hostname.includes('clientes') || window.location.href.includes('/portal');
    
    if (!isPortal && window.location.hostname !== 'localhost') {
      role = 'employee';
    }
    
    if (manualRole) role = manualRole;

    // 1. Crear o actualizar cuenta en bap_users
    if (!snap.exists()) {
      await setDoc(userRef, {
        uid: fbUser.uid,
        email: emailLower,
        name: fbUser.displayName || 'Nuevo Usuario',
        role: role,
        createdAt: serverTimestamp(),
        lastLogin: serverTimestamp()
      });
    } else {
      await setDoc(userRef, {
        lastLogin: serverTimestamp(),
        name: fbUser.displayName || snap.data()?.['name']
      }, { merge: true });
    }

    // 2. ENLACE CON LA TABLA bap_clients
    if (role === 'client' && emailLower) {
      const clientsRef = collection(this.firestore, 'bap_clients');
      // Buscamos si ya existe una ficha de cliente con ese correo
      const q = query(clientsRef, where('email', '==', emailLower));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        // JUGADA MAESTRA: Si el cliente no existe en el taller pero se creó un usuario, 
        // le creamos su ficha técnica automáticamente para que el taller lo vea.
        await addDoc(clientsRef, {
          name: fbUser.displayName || 'Cliente Portal',
          email: emailLower,
          phone: '',
          createdAt: serverTimestamp()
        });
        console.log("Ficha técnica de cliente creada para:", emailLower);
      } else {
        console.log("El cliente ya existía en el taller, enlace realizado por email.");
      }
    }
  }

  async logout(): Promise<void> {
    try {
      await signOut(this.afAuth);
      this.router.navigate(['/login']);
    } catch (error) {
      console.error('Falló el cierre de sesión:', error);
    }
  }

 async forgotPassword(email: string): Promise<void> {
    try {
      await sendPasswordResetEmail(this.afAuth, email);
    } catch (error) {
      console.error("Reset Password Error:", error);
      throw error;
    }
  }


}